import React from 'react';
import { useTranslation } from 'react-i18next';
import { UseQueryResult } from '@tanstack/react-query';

import CloseIcon from '@mui/icons-material/Close';
import { styled, useTheme } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Pagination from '@mui/material/Pagination';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Unstable_Grid2';

import { VizType } from 'business/definitions';
import Loading from 'components/Loading';
import IndicatorCard from '../IndicatorCard';
import {
    ChartsFilters,
    ChartsQueryResponse,
    ChartsTemplatesQueryResponse,
    DEFAULT_PAGE_SIZE,
} from '../../hooks/getCharts';
import { IndicatorFilterT } from '../../helpers/types';
import { concatenateSelectedValues } from '../helpers';

const StyFilterSummary = styled('div')(({ theme }) => ({
    backgroundColor: theme.palette.primary.background,
    borderRadius: theme.spacing(1),
    padding: theme.spacing(2),
}));

const StyDialog = styled(Dialog, {
    shouldForwardProp: (prop) => prop !== 'forceMinHeight',
})<{ forceMinHeight: boolean }>(({ theme, forceMinHeight }) => ({
    '& .MuiDialog-paper': {
        backgroundColor: theme.palette.lightGrey,
        minHeight: forceMinHeight ? '95vh' : undefined,
    },
}));

const StyDialogTitle = styled(DialogTitle)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
}));

const StyPagination = styled(Pagination)({
    '& .MuiPagination-ul': {
        justifyContent: 'right',
    },
});

const StyTypography = styled(Typography, {
    shouldForwardProp: (prop) => prop !== 'filter',
})<{ filter: string }>(({ theme, filter }) => ({
    color: filter.length > 0 ? 'inherit' : theme.palette.mediumGrey,
    display: '-webkit-box',
    fontWeight: 510,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: '2',
}));

type SeeMoreModalProps = {
    aggregationsFilter: IndicatorFilterT<Aggregation>[];
    integrationsFilter: IndicatorFilterT<string>[];
    isOpen: boolean;
    isModel: boolean;
    metricsFilter: string[];
    search: string;
    setIsOpen: (arg: boolean) => void;
    title: string;
    useGetCharts: (
        chartsFilters: ChartsFilters,
        page: number,
    ) => UseQueryResult<ChartsQueryResponse | ChartsTemplatesQueryResponse>;
    vizTypesFilter: IndicatorFilterT<VizType>[];
};

const SeeMoreModal = ({
    aggregationsFilter,
    integrationsFilter,
    isOpen,
    isModel,
    metricsFilter,
    search,
    setIsOpen,
    title,
    useGetCharts,
    vizTypesFilter,
}: SeeMoreModalProps): JSX.Element => {
    const { t } = useTranslation();
    const theme = useTheme();

    const [currentPage, setCurrentPage] = React.useState(1);

    const { data, isLoading } = useGetCharts(
        {
            aggregations: aggregationsFilter,
            integrations: integrationsFilter,
            metrics: metricsFilter,
            title: search || undefined,
            vizTypes: vizTypesFilter,
        },
        currentPage,
    );
    const filtersSummary = [
        {
            title: t('indicators.seeMoreIndicators.sectionTitles.vizType'),
            value: vizTypesFilter.map((fltr) => fltr.name).join(', '),
        },
        {
            title: t('indicators.seeMoreIndicators.sectionTitles.connector'),
            value: integrationsFilter.map((fltr) => fltr.name).join(', '),
        },
        {
            title: t('indicators.seeMoreIndicators.sectionTitles.metric'),
            value: concatenateSelectedValues(metricsFilter),
        },
        {
            title: t('indicators.seeMoreIndicators.sectionTitles.aggregation'),
            value: aggregationsFilter.map((fltr) => fltr.name).join(', '),
        },
        {
            title: t('indicators.seeMoreIndicators.sectionTitles.search'),
            value: search,
        },
    ];

    const indicators = data?.charts || [];
    const count = data?.count || 0;

    const PaginationBanner = (
        <StyPagination
            color="primary"
            count={Math.ceil(count / DEFAULT_PAGE_SIZE)}
            page={currentPage}
            onChange={(event, page) => setCurrentPage(page)}
        />
    );
    const IndicatorsGrid = isLoading ? (
        <Loading />
    ) : (
        count > 0 && (
            <>
                {PaginationBanner}
                <Grid container spacing={2} sx={{ mb: 1, mt: 1 }}>
                    {indicators.map((indicator) => (
                        <Grid
                            key={indicator.key}
                            lg={6}
                            xs={12}
                            sx={{ display: 'flex', justifyContent: 'center' }}
                        >
                            <IndicatorCard
                                indicator={indicator}
                                isModel={isModel}
                            />
                        </Grid>
                    ))}
                </Grid>
                {PaginationBanner}
            </>
        )
    );

    const handleClose = () => setIsOpen(false);

    return (
        <StyDialog
            fullWidth
            maxWidth="lg"
            open={isOpen}
            onClose={handleClose}
            forceMinHeight={isLoading}
        >
            <StyDialogTitle>
                <div
                    style={{
                        alignItems: 'center',
                        display: 'flex',
                        justifyContent: 'space-between',
                    }}
                >
                    <Typography variant="h5">{title}</Typography>
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </div>
                <StyFilterSummary>
                    <Typography
                        sx={{ mb: 2, color: theme.palette.primary.main }}
                        variant="body1Bold"
                    >
                        {t('indicators.seeMoreIndicators.filteredOn')}
                    </Typography>
                    <Grid container columns={24} spacing={1}>
                        {filtersSummary.map((fltr) => (
                            <>
                                <Grid xs={3}>
                                    <Typography>{fltr.title}</Typography>
                                </Grid>
                                <Grid xs={9}>
                                    <Tooltip
                                        enterDelay={500}
                                        placement="bottom-start"
                                        title={fltr.value}
                                    >
                                        <StyTypography filter={fltr.value}>
                                            {fltr.value ||
                                                t(
                                                    'indicators.seeMoreIndicators.noFiltersApplied',
                                                )}
                                        </StyTypography>
                                    </Tooltip>
                                </Grid>
                            </>
                        ))}
                    </Grid>
                </StyFilterSummary>
            </StyDialogTitle>
            <DialogContent>{IndicatorsGrid}</DialogContent>
        </StyDialog>
    );
};

export default SeeMoreModal;
